import React, {useEffect, useRef, useState} from "react";
import {Badge, Button, Drawer, List, Tag, Typography} from "antd";
import {SoundOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {selectActiveCount, selectActiveExchanges,} from "../../../../store/exchangeActiveSlice";
import {formatUtc} from "../utils/time";

const {Text} = Typography;

function normalizeStatus(s) {
  return String(s || "").toUpperCase();
}

function formatMoneyLike(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}


function statusTag(status) {
  const s = normalizeStatus(status);

  if (s === "DISPUTE") return <Tag color="red">Спор</Tag>;
  if (s === "CANCELLED") return <Tag>Отменён</Tag>;
  if (s === "COMPLETED") return <Tag color="green">Завершён</Tag>;
  if (s === "IN_PROGRESS") return <Tag color="blue">В процессе</Tag>;
  if (s === "CREATED") return <Tag color="blue">Создан</Tag>;

  return <Tag>{s || "—"}</Tag>;
}

export default function ExchangeNotifierBar() {
  const navigate = useNavigate();

  const activeExchanges = useSelector(selectActiveExchanges);
  const count = useSelector(selectActiveCount);

  const [open, setOpen] = useState(false);

  const prevCountRef = useRef(0);
  const [animKey, setAnimKey] = useState(0);
  const [pulseKey, setPulseKey] = useState(0);

  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = count;

    if (count <= 0) return;

    const firstAppear = prev === 0;
    const increased = count > prev;

    if (firstAppear || increased) triggerAttention();
  }, [count]);

  useEffect(() => {
    if (count <= 0) return;
    const interval = setInterval(() => triggerAttention(), 5000);
    return () => clearInterval(interval);
  }, [count]);

  function triggerAttention() {
    setAnimKey((k) => k + 1);
    setPulseKey((k) => k + 1);
  }

  useEffect(() => {
    if (count === 0) setOpen(false);
  }, [count]);

  if (!count) return null;

  return (
    <>
      <style>{`
        @keyframes exhb-shake-lite {
          0% { transform: translateY(10px) scale(0.985); opacity: 0.92; }
          22% { transform: translateY(0) scale(1); opacity: 1; }
          35% { transform: translateX(-4px); }
          50% { transform: translateX(4px); }
          65% { transform: translateX(-2px); }
          80% { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }
        @keyframes exhb-breathe {
          0%,100% { filter: brightness(1); }
          50% { filter: brightness(1.06); }
        }
        @keyframes exhb-pulse-ring {
          0% { transform: scale(0.90); opacity: 0; }
          30% { opacity: 0.32; }
          100% { transform: scale(1.20); opacity: 0; }
        }
        @keyframes exhb-badge-pop {
          0% { transform: scale(1); }
          30% { transform: scale(1.22); }
          60% { transform: scale(0.97); }
          100% { transform: scale(1); }
        }

        .exh-sheet .ant-drawer-content {
          border-top-left-radius: 18px;
          border-top-right-radius: 18px;
          overflow: hidden;
        }
        .exh-sheet .ant-drawer-header { padding: 14px 16px; }
        .exh-sheet .ant-drawer-body { padding: 12px 16px 18px; }

        .exh-sheet-list .ant-list-item {
          padding: 0 !important;
          border: none !important;
          margin-bottom: 12px;
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          left: 16,
          right: 16,
          bottom: 16,
          zIndex: 2000,
          pointerEvents: "none",
        }}
      >
        <div style={{pointerEvents: "auto"}} onClick={() => setOpen(true)}>
          <div
            key={animKey}
            style={{
              position: "relative",
              cursor: "pointer",
              userSelect: "none",
              background: "var(--blue-3)",
              color: "#fff",
              borderRadius: 14,
              padding: "12px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              animation:
                "exhb-shake-lite 800ms ease-out, exhb-breathe 1600ms ease-in-out infinite",
            }}
          >
            <div
              key={pulseKey}
              style={{
                position: "absolute",
                inset: -6,
                borderRadius: 18,
                border: "2px solid var(--blue-3)",
                pointerEvents: "none",
                animation: "exhb-pulse-ring 900ms ease-out",
              }}
            />

            <div style={{display: "flex", alignItems: "center", gap: 10}}>
              <SoundOutlined style={{fontSize: 20}}/>
              <div>
                <div style={{fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center"}}>
                  Активные обмены
                  <span style={{marginLeft: 10}}>
                    <Badge
                      count={count}
                      style={{
                        backgroundColor: "#FF4D4F",
                        minWidth: 28,
                        height: 28,
                        lineHeight: "28px",
                        fontSize: 15,
                        fontWeight: 900,
                        animation: "exhb-badge-pop 450ms ease-out",
                      }}
                    />
                  </span>
                </div>
                <div style={{opacity: 0.9, fontSize: 12}}>Нажмите, чтобы открыть список</div>
              </div>
            </div>

            <div
              style={{
                fontWeight: 800,
                fontSize: 13,
                background: "rgba(255,255,255,0.18)",
                padding: "8px 10px",
                borderRadius: 10,
                whiteSpace: "nowrap",
              }}
            >
              Открыть
            </div>
          </div>
        </div>
      </div>

      <Drawer
        className="exh-sheet"
        placement="bottom"
        open={open}
        onClose={() => setOpen(false)}
        height="72vh"
        title={
          <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10}}>
            <div style={{fontWeight: 900}}>Активные обмены</div>
            <div style={{color: "rgba(2,6,23,0.55)", fontWeight: 800}}>{count} шт.</div>
          </div>
        }
      >
        <List
          className="exh-sheet-list"
          dataSource={activeExchanges}
          renderItem={(x) => (
            <List.Item>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(`/exchange/flow/${x.id}`);
                }}
                className={[
                  "w-full text-left",
                  "rounded-[16px] border border-black/5 bg-white",
                  "px-4 py-4",
                  "hover:bg-[#F7FAFF] hover:border-[#0B63F6]/15",
                  "transition-colors",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Text strong>Обмен</Text>
                      <Text code className="max-w-[210px] truncate" title={x.id}>
                        {x.id}
                      </Text>
                      {statusTag(x.status)}
                    </div>

                    <div className="mt-2 text-[18px] font-extrabold text-[#0B1B3A]">
                      {formatMoneyLike(x.amount_from)} {x.from.currency}{" "}
                      <span className="text-black/40">→</span>{" "}
                      {formatMoneyLike(x.amount_to)} {x.to.currency}
                    </div>

                    <div className="mt-1 text-[12px] text-black/55">
                      {x.from.currency} · {x.from.method} → {x.to.currency} · {x.to.method}
                    </div>

                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                      <div className="text-[12px] text-black/60">
                        <span className="text-black/45">Код:</span> <b>{x.code}</b>
                      </div>
                      <div className="text-[12px] text-black/60">
                        <span className="text-black/45">Создан:</span> <b>{formatUtc(x.created_at)}</b>
                      </div>
                      <div className="text-[12px] text-black/60">
                        <span className="text-black/45">Реквизит:</span>{" "}
                        <b>{x.payout_type} · {x.payout_detail}</b>
                      </div>
                      <div className="text-[12px] text-black/60">
                        <span className="text-black/45">Курс:</span>{" "}
                        <b>покупка {x.buy_rate ?? "—"} / продажа {x.sell_rate ?? "—"}</b>
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0">
                    <div className="rounded-[12px] px-3 py-2 bg-[#F3F6FF] text-[#0B63F6] font-extrabold">
                      Открыть
                    </div>
                  </div>
                </div>
              </button>
            </List.Item>
          )}
        />

        <div className="mt-2 flex justify-end">
          <Button onClick={() => setOpen(false)}>Закрыть</Button>
        </div>
      </Drawer>
    </>
  );
}